import { useQuery } from "@tanstack/react-query";

import { api, type KpiDefinition, type ReportScope } from "../../../lib/api";
import type { DashboardLayout } from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { Select } from "../../ui/Select";
import {
  Delta,
  RangeFilter,
  formatMinutes,
  rangeIsReady,
  toRangeValue,
} from "../RangeFilter";

/**
 * The four figures at the top, each of which can be swapped for another.
 *
 * Interchangeable slots rather than four fixed tiles, because what belongs at
 * the top of a dashboard is not the same for everybody: a team lead watching a
 * backlog wants open and about-to-breach, and somebody reviewing last quarter
 * wants compliance and median response. Both of those are the top of the page
 * for the person looking at it.
 *
 * Each slot carries its own date range, like every other card. That is the
 * point of dropping the page-level filter: "open tickets right now" beside "SLA
 * compliance this quarter" is a perfectly ordinary thing to want, and a single
 * page filter makes it impossible.
 */

const SLOTS = 4;

export function KpiRow({
  layout,
  scope,
}: {
  layout: DashboardLayout;
  scope: ReportScope;
}) {
  const catalogue = useQuery({
    queryKey: queryKeys.dashboardKpiMetrics,
    queryFn: api.kpiMetrics,
    staleTime: 30 * 60_000,
  });

  const metrics = catalogue.data?.metrics ?? [];
  const defaults = catalogue.data?.defaults ?? [];

  return (
    /* Two up even on the narrowest phone. One per row made four full-width
       tiles of a single number each, which is most of a handset screen spent
       saying four things -- and it was the main reason the pinned header
       swallowed the page. */
    <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
      {Array.from({ length: SLOTS }, (_, index) => (
        <Slot
          key={index}
          slotId={`kpi:${index}`}
          fallback={defaults[index] ?? metrics[index]?.id ?? "open"}
          metrics={metrics}
          layout={layout}
          scope={scope}
        />
      ))}
    </div>
  );
}

function Slot({
  slotId,
  fallback,
  metrics,
  layout,
  scope,
}: {
  slotId: string;
  fallback: string;
  metrics: KpiDefinition[];
  layout: DashboardLayout;
  scope: ReportScope;
}) {
  const stored = layout.card(slotId);
  /* A stored metric that no longer exists falls back rather than rendering an
     empty tile -- the catalogue can lose an entry between releases. */
  const metric =
    metrics.find((entry) => entry.id === stored.metric)?.id ?? fallback;
  const definition = metrics.find((entry) => entry.id === metric);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);

  const result = useQuery({
    queryKey: queryKeys.dashboardReport("kpi", range, scope, metric),
    queryFn: () => api.kpi(metric, range, scope),
    enabled: ready && metrics.length > 0,
    /* Live figures describe the queue as it is, so they go stale immediately
       and are re-asked on a timer; a period figure will not have moved. */
    staleTime: definition?.live ? 0 : DASHBOARD_STALE_MS,
    refetchInterval: definition?.live ? DASHBOARD_STALE_MS : false,
  });

  const value = result.data?.value ?? null;

  return (
    /* `min-w-0`: a grid item defaults to `min-width: auto`, so without it the
       track grows to fit the metric select rather than the select shrinking. */
    <section className="min-w-0 rounded-lg border border-line bg-surface p-3">
      {/*
        On a phone the select is invisible and laid over a label of our own,
        for the reason `RangeFilter` gives: a drawn select's uppercase "Within
        15 min of breach" in a half-width tile was cut off mid-word. The
        label truncates properly instead, and the tap still opens the
        platform's picker. A desktop keeps the plain select.
      */}
      <div className="relative mb-1 flex items-start gap-1">
        <span
          aria-hidden
          className="flex min-w-0 flex-1 items-center gap-1 text-[11px] uppercase tracking-wide text-faint md:hidden"
        >
          <span className="truncate">{definition?.label ?? "\u00a0"}</span>
          <span className="shrink-0 text-[9px]">{"\u25be"}</span>
        </span>
        <Select
          value={metric}
          onChange={(next) => layout.setCard(slotId, { metric: next })}
          label="Which figure this tile shows"
          className="min-w-0 flex-1 border-transparent bg-transparent !px-0 text-[11px] uppercase tracking-wide text-faint hover:text-muted max-md:absolute max-md:inset-0 max-md:h-full max-md:w-full max-md:opacity-0"
          options={metrics.map((entry) => ({
            value: entry.id,
            label: entry.label,
          }))}
        />
      </div>

      <p className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums text-content">
          {format(value, definition)}
        </span>
        {/* No arrow on a live figure. The queue was not "shorter last month" --
            it is the length it is now, and an arrow would be invented. */}
        {definition?.live ? null : (
          <Delta
            points={result.data?.delta ?? null}
            higherIsBetter={definition?.higherIsBetter ?? true}
            suffix={definition?.unit === "rate" ? "pts" : "%"}
          />
        )}
      </p>

      <div className="mt-0.5 flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-[11px] text-faint">
          {/* The population, stated. "12 over SLA" against an unnamed
              denominator is a number nobody can act on. */}
          {result.data?.measured !== undefined && definition?.caption
            ? `${result.data.measured} ${definition.caption}`
            : (definition?.caption ?? " ")}
        </p>

        {definition?.live ? (
          <span className="shrink-0 text-[11px] text-faint">now</span>
        ) : (
          <span className="shrink-0">
            <RangeFilter
              value={range}
              onChange={(next) => layout.setCard(slotId, { range: next })}
            />
          </span>
        )}
      </div>
    </section>
  );
}

/** A value in the shape its unit calls for. */
function format(value: number | null, definition?: KpiDefinition): string {
  if (value === null) return "—";
  if (!definition) return String(value);

  switch (definition.unit) {
    case "rate":
      return `${Math.round(value * 100)}%`;
    case "minutes":
      return formatMinutes(value);
    default:
      return value.toLocaleString();
  }
}
